import React, { memo, useEffect, useState, useContext } from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { IRow } from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import EkonomiskAnalysRow from '../EkonomiskAnalysRow/EkonomiskAnalysRow';
import EDTable from '../../../../../../../BaseComponents/Table/EDTable';
import EDTableHead from '../../../../../../../BaseComponents/Table/Components/EDTableHead';
import EDTableRow from '../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EkonomiskAnalysTableCell from '../EkonomiskAnalysTableCell/EkonomiskAnalysTableCell';
import EDTableBody from '../../../../../../../BaseComponents/Table/Components/EDTableBody';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';
import { getCurrencyFormatterParams } from '../../Utils/paramUtils';
import { tableIndexToColumnIndex } from '../../Utils/tableUtils';
import EkonomiskAnalysDetailInfo from '../AnalysDetailInfo/EkonomiskAnalysDetailInfo';
import {
  IFilledLocalAnalys,
  ILocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import { IColumn } from '../EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import { equals } from 'ramda';
import { IUIUrval, TEDUrvalType } from '../../../../../../../Types/UrvalTypes';
import EkonomiskAnalysRowsWarningDialog, {
  EKONOMISK_ANALYS_ROWS_WARNING_LIMIT,
  getIgnoreRowsWarning,
} from '../EkonomiskAnalysRowsWarningDialog/EkonomiskAnalysRowsWarningDialog';
import EDTableCell from '../../../../../../../BaseComponents/Table/Components/EDTableCell';
import { getFieldOperatorData } from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQueryUtils';
import { TECKENVAND_KOLUMN } from '../../Const/EkonomiskAnalysConsts';
import classNames from 'classnames';
import { ISortInfo } from '../../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import EDTableSortLabel from '../../../../../../../BaseComponents/Table/Components/EDTableSortLabel';
import { EkonomiskAnalysContext } from '../Context/EkonomiskAnalysContext';
import { getForceHelarsJamforelse } from '../../Utils/userInfoUtils';

interface IProps {
  columns: IColumn[];
  rows: IRow[];
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  totalSumRow?: IRow;
  analysSettings: IFilledLocalAnalys;
  showComparisonNumber: boolean;
  updatedAnalysSettingsUrval: IUIUrval | null;
  expandedLevel: number;
  onDenominationChange: (denomination: number) => void;
  selectedAnalys: ISelectedAnalys;
  tableHeaderLeftArea?: React.ReactNode;
  isSubTable?: boolean;
  detailedSelection?: TEDUrvalType;
  page?: number;
  rowsPerPage?: number;
  updatedAnalysSettings: ILocalAnalys;
  utanSummeringSumColCount?: number;
  sortInfo?: ISortInfo<IRow>;
  createSortHandler?: (orderBy: string, caption: string) => () => void;
  classes?: any;
  sammanfattningsMode?: boolean;
}

const styles = (theme: Theme) => ({
  scrollContainer: {
    width: '100%',
    flex: '0 2 auto',
    backgroundColor: theme.palette.common.white,
    overflow: 'auto',
    '-webkit-overflow-scrolling': 'touch',
  },
  inline: {
    display: 'inline',
    whiteSpace: 'nowrap',
  },
  spacingRight: {
    paddingRight: 16,
  },
  alignStart: {
    alignSelf: 'flex-start',
  },
  columnTitle: {
    fontWeight: 'bold',
    color: theme.customProperties.Fade.DarkOnLight.mediumEmphasis,
    // color: 'rgba(0,0,0,0.54)',
  },
  table: {
    tableLayout: 'auto',
  },
  firstColumnHeadCell: {
    '&&&': {
      width: 100,
      paddingBottom: '12px',
    },
  },
  columnHead: { '&&&': { width: 75, paddingBottom: '12px' } },
  lastColumnHeadCell: {
    '&:last-child': {
      padding: 0,
      top: 0,
      zIndex: 120,
      position: 'sticky',
      verticalAlign: 'bottom',
      backgroundColor: theme.palette.common.white,
    },
  },
  lastColumnCell: {
    padding: 0,
  },
  smallColumnHead: {
    fontSize: '0.75rem',
    fontWeight: 900,
  },
  lastSumCol: {
    borderRight:
      '1px solid ' + theme.customProperties.EkonomiskAnalysTableBorderColor,
  },
});

const EkonomiskAnalysLopandeTable = (props: IProps) => {
  const ekonomiskAnalysContext = useContext(EkonomiskAnalysContext);
  const [rowWarningDialogOpen, setRowWarningDialogOpen] = useState(false);

  useEffect(() => {
    if (props.rows.length > EKONOMISK_ANALYS_ROWS_WARNING_LIMIT) {
      getIgnoreRowsWarning().then(ignoreWarning => {
        if (!ignoreWarning) {
          setRowWarningDialogOpen(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visaSummeringar = props.analysSettings.visaSummeringar;

  const page = props.page ?? 0;
  const rowsPerPage = props.rowsPerPage ?? 0;

  return (
    <>
      <div className={props.classes.scrollContainer} data-scroll="">
        <EDTable stretch noBorderCollapse className={props.classes.table}>
          <EDTableHead>
            <EDTableRow>
              {props.columns.map((x, i) => {
                let shouldAdd = true;
                if (props.rows && props.rows[0]) {
                  shouldAdd =
                    i === 0 ||
                    i > props.columns.length - props.rows[0].data.length ||
                    !visaSummeringar;
                }

                const isBeraknad = getFieldOperatorData(x.fieldInfo) !== null;
                let tooltipTitle = '';
                if (isBeraknad && x.inverted) {
                  tooltipTitle = `Värde kan ej visas om de kolumner som beräkningen utgår från ej ingår i analysen eller saknar värde\n${TECKENVAND_KOLUMN}`;
                } else if (isBeraknad) {
                  tooltipTitle =
                    'Värde kan ej visas om de kolumner som beräkningen utgår från ej ingår i analysen eller saknar värde';
                } else if (x.inverted) {
                  tooltipTitle = TECKENVAND_KOLUMN;
                }

                const sortProperty = props.sortInfo?.properties.find(
                  x => x.orderBy === i.toString()
                );
                const sortIndex =
                  props.sortInfo?.properties &&
                  props.sortInfo?.properties.length > 1 &&
                  sortProperty
                    ? props.sortInfo?.properties?.indexOf(sortProperty) + 1
                    : undefined;

                const typographyComponent = (
                  <EDTypography
                    variant={'body2'}
                    className={classNames(props.classes.columnTitle, {
                      [props.classes.smallColumnHead]:
                        !visaSummeringar &&
                        i <
                          (props.utanSummeringSumColCount ??
                            props.analysSettings.sums.length),
                    })}
                    noWrap
                  >
                    {x.title1}
                    {x.title2 && <br />}
                    {x.title2}
                    {x.title3 && <br />}
                    {x.title3}
                  </EDTypography>
                );

                const typographyComponentWithSortLabel = (
                  <EDTableSortLabel
                    direction={sortProperty ? sortProperty.order : undefined}
                    sortIndex={sortIndex}
                    active={Boolean(sortProperty)}
                  >
                    {typographyComponent}
                  </EDTableSortLabel>
                );

                const usedTypographyComponent = visaSummeringar
                  ? typographyComponent
                  : typographyComponentWithSortLabel;

                return (
                  shouldAdd && (
                    <EkonomiskAnalysTableCell
                      key={x.title1 + x.title2 + x.title3 + i}
                      variant="head"
                      subTableHeader={props.isSubTable}
                      alignment={
                        i <
                        (props.utanSummeringSumColCount ??
                          props.analysSettings.sums.length)
                          ? 'left'
                          : 'right'
                      }
                      stickyTop={0}
                      paddingLeft={!visaSummeringar ? 32 : i === 0 ? 0 : 24}
                      noVerticalSpacing
                      className={classNames({
                        [props.classes.firstColumnHeadCell]:
                          i === 0 && visaSummeringar,
                        [props.classes.columnHead]: !visaSummeringar || i !== 0,
                        [props.classes.lastSumCol]:
                          !visaSummeringar &&
                          i ===
                            (props.utanSummeringSumColCount ??
                              props.analysSettings.sums.length) -
                              1,
                      })}
                      onClick={
                        visaSummeringar
                          ? undefined
                          : props.createSortHandler?.(
                              i.toString(),
                              'Testing caption'
                            )
                      }
                    >
                      <EDTooltip title={tooltipTitle}>
                        <div>
                          {(i !== 0 || !visaSummeringar) &&
                            usedTypographyComponent}
                          {i === 0 &&
                            !props.tableHeaderLeftArea &&
                            visaSummeringar && (
                              <EkonomiskAnalysDetailInfo
                                showComparsion={props.showComparisonNumber}
                                impersonatedCodePrivilege={
                                  props.impersonatedCodePrivilege
                                }
                                analysSettings={props.analysSettings}
                                denomination={
                                  props.updatedAnalysSettings.other.denomination
                                }
                                updatedAnalysSettingsUrval={
                                  props.updatedAnalysSettingsUrval
                                }
                                updateImpersonatedCodePrivilege={
                                  props.updateImpersonatedCodePrivilege
                                }
                                onDenominationChange={
                                  props.onDenominationChange
                                }
                                selectedAnalys={props.selectedAnalys}
                                sammanfattningsMode={props.sammanfattningsMode}
                              />
                            )}
                          {i === 0 &&
                            props.tableHeaderLeftArea &&
                            props.tableHeaderLeftArea}
                        </div>
                      </EDTooltip>
                    </EkonomiskAnalysTableCell>
                  )
                );
              })}
              <EDTableCell className={props.classes.lastColumnHeadCell} />
            </EDTableRow>
          </EDTableHead>

          <EDTableBody>
            {(visaSummeringar ? props.rows : props.sortInfo?.rows ?? [])
              .slice(
                visaSummeringar ? undefined : page * rowsPerPage,
                visaSummeringar ? undefined : page * rowsPerPage + rowsPerPage
              )
              .map((x, index) => (
                <EkonomiskAnalysRow
                  codePrivilegesId={
                    props.impersonatedCodePrivilege
                      ? props.impersonatedCodePrivilege.id
                      : props.selectedAnalys.codePrivilege.id
                  }
                  key={'row-' + index}
                  columns={props.columns}
                  row={x}
                  level={
                    visaSummeringar ? 0 : props.analysSettings.sums.length - 1
                  }
                  analysSettings={props.analysSettings}
                  dates={props.analysSettings.dates}
                  denomination={props.updatedAnalysSettings.other.denomination}
                  nrDecimals={props.updatedAnalysSettings.other.decimals}
                  endastBokfort={
                    !props.analysSettings.other.includeEjBokfordaJournaler
                  }
                  expandedLevel={props.expandedLevel}
                  grundUrvalId={
                    props.selectedAnalys.analys.grundurval
                      ? props.selectedAnalys.analys.grundurval.id
                      : undefined
                  }
                  visaSummeringar={visaSummeringar}
                  utanSummeringSumColCount={props.utanSummeringSumColCount}
                  detailedSelection={props.detailedSelection}
                  forceHelarsJamforelse={getForceHelarsJamforelse(
                    ekonomiskAnalysContext.harPeriodiseradBudget,
                    ekonomiskAnalysContext.userInfo?.preferHelarsjamforelse
                  )}
                />
              ))}

            {props.totalSumRow && (
              <EDTableRow hover>
                {props.totalSumRow.data.map((d, i) => {
                  const formatter =
                    i > 0
                      ? d.displayType === 'percent'
                        ? findFormatter('TEDFormatIntAsPercent')
                        : findFormatter('TEDFormatCurr')
                      : undefined;

                  const formatterParams =
                    d.displayType === 'percent'
                      ? 'Decimals="1"'
                      : getCurrencyFormatterParams(
                          props.columns[
                            tableIndexToColumnIndex(
                              i,
                              props.utanSummeringSumColCount ??
                                props.analysSettings.sums.length,
                              visaSummeringar
                            )
                          ],
                          props.updatedAnalysSettings.other.denomination,
                          props.updatedAnalysSettings.other.decimals
                        );

                  return (
                    <EkonomiskAnalysTableCell
                      key={'total-sum-cell-' + i}
                      alignment={i > 0 ? 'right' : 'left'}
                      style={{
                        paddingRight: 15,
                      }}
                      textVariant="subtitle2"
                      value={d.value}
                      formatter={formatter}
                      formatterParams={formatterParams}
                    />
                  );
                })}
                <EDTableCell className={props.classes.lastColumnCell} />
              </EDTableRow>
            )}
          </EDTableBody>
        </EDTable>
      </div>
      <EkonomiskAnalysRowsWarningDialog
        open={rowWarningDialogOpen}
        setOpen={setRowWarningDialogOpen}
      />
    </>
  );
};

export default memo(
  withStyles(EkonomiskAnalysLopandeTable, styles),
  (prevProps, nextProps) => equals(prevProps, nextProps)
);
